import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../config/api';
import useAnalyticsAccess from '../../hooks/useAnalyticsAccess';
import { Page, Container, Eyebrow, GhostButton, PrimaryButton, DISCORD_DM } from '../Layout/PageLayout';

// Display info per product_id; add new shop products here. Unknown ids fall back to a readable name.
const PRODUCTS = {
  shop_gifting_system: { name: 'Gifting System', image: '/images/shop/gifting.png' },
};

const productInfo = (id) =>
  PRODUCTS[id] || {
    name: id
      .replace(/^shop_/, '')
      .split(/[_-]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' '),
  };

// D1 returns "YYYY-MM-DD HH:MM:SS" in UTC without a zone marker.
const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('Z') ? '' : 'Z'));
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const Wrap = styled(Container)`
  padding-top: 3.5rem;
  padding-bottom: 6rem;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.25rem 2rem;
  margin-bottom: 2.5rem;

  h1 {
    font-size: clamp(2.25rem, 4.5vw, 3.25rem);
    line-height: 1.05;
    letter-spacing: -0.035em;
    margin-bottom: 0.625rem;
  }
`;

const Layout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: flex-start;
`;

const Panel = styled.section`
  flex: 999 1 38.75rem;
  min-width: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-tertiary);
`;

const Rows = styled.ul`
  list-style: none;
`;

const Row = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.25rem;
  padding: 1.375rem 1.5rem;
  border-bottom: 1px solid var(--border-subtle);

  &:last-child {
    border-bottom: 0;
  }
`;

const Thumb = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: var(--surface-3);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Info = styled.div`
  flex: 1 1 16.25rem;
  min-width: 0;

  strong {
    display: block;
    font-size: 1.0625rem;
    margin-bottom: 0.25rem;
  }

  div {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    font-size: 0.8125rem;
    color: var(--text-tertiary);
  }
`;

const OrderId = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
`;

const DownloadButton = styled.button`
  min-height: 2.75rem;
  padding: 0 1.125rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${(p) => (p.$again ? 'transparent' : 'var(--brand)')};
  color: ${(p) => (p.$again ? 'var(--text-primary)' : 'var(--brand-ink)')};
  border: ${(p) => (p.$again ? '1px solid var(--border-accent)' : '0')};

  &:hover:not(:disabled) {
    background: ${(p) => (p.$again ? 'transparent' : '#ff8f4f')};
    border-color: var(--text-tertiary);
  }

  &:disabled {
    opacity: 0.7;
    cursor: progress;
  }
`;

const Skeleton = styled.span`
  display: block;
  border-radius: ${(p) => p.$r || '0.25rem'};
  background: var(--surface-3);
  width: ${(p) => p.$w};
  height: ${(p) => p.$h};
  flex-shrink: 0;
`;

const Empty = styled.div`
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;

  > div {
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    border: 1px solid var(--border-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-tertiary);
  }

  h2 {
    font-size: 1.5rem;
    margin-top: 0.5rem;
  }

  p {
    max-width: 22.5rem;
    margin-bottom: 0.75rem;
  }
`;

const Alert = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1.5rem;
  padding: 0.875rem 1.125rem;
  margin-bottom: 1.25rem;
  border: 1px solid rgba(240, 82, 82, 0.35);
  background: rgba(240, 82, 82, 0.08);
  border-radius: 0.5rem;
  color: var(--error);
  font-size: 0.9375rem;

  button {
    min-height: 2.5rem;
    padding: 0 1rem;
    background: transparent;
    border: 1px solid rgba(240, 82, 82, 0.5);
    border-radius: 0.375rem;
    color: var(--error);
    font-size: 0.875rem;
  }
`;

const Side = styled.aside`
  flex: 1 1 18.75rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SideCard = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 1.375rem;

  h2 {
    font-size: 1.125rem;
    margin-bottom: 0.875rem;
  }

  p {
    font-size: 0.875rem;
    margin: -0.375rem 0 1rem;
  }
`;

const Steps = styled.ol`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--text-secondary);

  li {
    display: flex;
    gap: 0.75rem;
  }

  span {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--brand);
    padding-top: 2px;
  }
`;

const AnalyticsLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.375rem;
  background: var(--surface-3);
  border: 1px solid var(--brand);
  border-radius: 0.75rem;
  color: var(--text-primary);

  strong,
  small {
    display: block;
  }

  small {
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  &:hover {
    background: #1a1d22;
  }
`;

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
  </svg>
);

const BoxIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 8l-9-5-9 5 9 5 9-5z" />
    <path d="M3 8v8l9 5 9-5V8" />
    <path d="M12 13v8" />
  </svg>
);

const Dashboard = () => {
  const [purchases, setPurchases] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const analyticsAllowed = useAnalyticsAccess(user);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchPurchases = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      navigate('/login');
      return;
    }

    setInitialLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/user/purchases`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_email');
          navigate('/login');
          return;
        }

        let message = 'Failed to fetch purchases';
        try {
          const errorData = await response.json();
          message = errorData.error || message;
        } catch {}
        throw new Error(message);
      }

      const data = await response.json();
      setPurchases(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Dashboard error:', err);
      setPurchases([]);
      setError(err.message || 'Something went wrong while loading your dashboard.');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleDownload = async (productId) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/login');
        return;
      }

      setDownloadLoadingId(productId);
      setError('');

      const response = await fetch(`${API_BASE}/api/download?file=${encodeURIComponent(productId)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }

        let message = 'Download failed';
        try {
          const errorData = await response.json();
          message = errorData.error || message;
        } catch {}
        throw new Error(message);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${productId}.rbxl`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Mirror the server's last_download update without refetching.
      const now = new Date().toISOString();
      setPurchases((list) => list.map((p) => (p.product_id === productId ? { ...p, last_download: now } : p)));
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Download failed');
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const count = purchases.length;

  return (
    <Page>
      <Wrap>
        <Head>
          <div>
            <Eyebrow>Dashboard</Eyebrow>
            <h1>Your products</h1>
            <p>Your purchased files are available to download at any time.</p>
          </div>
          <GhostButton to="/shop">Browse the shop</GhostButton>
        </Head>

        {error && (
          <Alert role="alert">
            <span>
              {error}
              {count > 0 && '. If the problem continues, send me your order number on Discord.'}
            </span>
            {count > 0 ? (
              <button type="button" onClick={() => setError('')}>
                Dismiss
              </button>
            ) : (
              <button type="button" onClick={fetchPurchases}>
                Try again
              </button>
            )}
          </Alert>
        )}

        <Layout>
          <Panel aria-label="Products">
            <PanelHead>
              <span>{initialLoading ? 'Loading…' : `${count} ${count === 1 ? 'product' : 'products'}`}</span>
              <span>Roblox place files (.rbxl)</span>
            </PanelHead>

            {initialLoading ? (
              <Rows aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <Row key={i}>
                    <Skeleton $w="3.5rem" $h="3.5rem" $r="0.5rem" />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <Skeleton $w="40%" $h="14px" />
                      <Skeleton $w="60%" $h="10px" />
                    </div>
                    <Skeleton $w="8.25rem" $h="2.75rem" $r="0.375rem" />
                  </Row>
                ))}
              </Rows>
            ) : count === 0 ? (
              <Empty>
                <div>
                  <BoxIcon size={26} />
                </div>
                <h2>No products yet</h2>
                <p>Items you buy in the shop appear here with a download button.</p>
                <PrimaryButton to="/shop">Browse the shop</PrimaryButton>
              </Empty>
            ) : (
              <Rows>
                {purchases.map((purchase) => {
                  const info = productInfo(purchase.product_id);
                  const busy = downloadLoadingId === purchase.product_id;
                  const last = formatDate(purchase.last_download);
                  return (
                    <Row key={purchase.id}>
                      <Thumb>{info.image ? <img src={info.image} alt="" /> : <BoxIcon />}</Thumb>
                      <Info>
                        <strong>{info.name}</strong>
                        <div>
                          <span>Purchased {formatDate(purchase.purchase_date) || '-'}</span>
                          <span>{last ? `Last downloaded ${last}` : 'Not downloaded yet'}</span>
                          {purchase.order_id && <OrderId>Order {purchase.order_id}</OrderId>}
                        </div>
                      </Info>
                      <DownloadButton
                        type="button"
                        $again={Boolean(last)}
                        disabled={busy}
                        onClick={() => handleDownload(purchase.product_id)}
                      >
                        <DownloadIcon />
                        {busy ? 'Preparing…' : last ? 'Download again' : 'Download'}
                      </DownloadButton>
                    </Row>
                  );
                })}
              </Rows>
            )}
          </Panel>

          <Side>
            {analyticsAllowed && (
              <AnalyticsLink to="/dashboard/analytics">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                <span style={{ flexGrow: 1 }}>
                  <strong>Game analytics</strong>
                  <small>Player sessions and funnels</small>
                </span>
                <span aria-hidden="true" style={{ color: 'var(--text-secondary)' }}>
                  →
                </span>
              </AnalyticsLink>
            )}

            <SideCard>
              <h2>Using your files</h2>
              <Steps>
                <li><span>01</span>Download the .rbxl file</li>
                <li><span>02</span>Open it in Roblox Studio</li>
                <li><span>03</span>Copy the system into your own place</li>
              </Steps>
            </SideCard>

            <SideCard>
              <h2>Need help?</h2>
              <p>If a file does not work, send me your order number on Discord.</p>
              <GhostButton
                as="a"
                href={DISCORD_DM}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: '100%', fontSize: '0.875rem' }}
              >
                Message on Discord
              </GhostButton>
            </SideCard>
          </Side>
        </Layout>
      </Wrap>
    </Page>
  );
};

export default Dashboard;
